/**
 * ValidationCelebration Component
 *
 * A scan-complete popup shown when validation finishes, displaying
 * how many elements were scanned against how many rules.
 */

import { useState } from "react";

interface ValidationCelebrationProps {
  passedCount: number;
  elementsScanned?: number;
  rulesCompared?: number;
  onClose: () => void;
  onViewResults?: () => void;
}

export function ValidationCelebration({
  passedCount,
  elementsScanned = 0,
  rulesCompared = 0,
  onClose,
  onViewResults,
}: ValidationCelebrationProps) {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleViewResults = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      onViewResults?.();
    }, 300);
  };

  const summaryText =
    elementsScanned > 0 && rulesCompared > 0
      ? `${elementsScanned} element${elementsScanned === 1 ? "" : "s"} scanned against ${rulesCompared} rule${rulesCompared === 1 ? "" : "s"} with the click of a button`
      : "You have just saved a week with a click of a button";

  if (!isVisible) return null;

  return (
    <>
      <style>{`
        @keyframes cel-fade-in {
          0% { opacity: 0; transform: scale(0.8); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes cel-fade-out {
          0% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        @keyframes cel-glow {
          0%, 100% { text-shadow: 0 0 10px rgba(255,255,255,0.5); }
          50% { text-shadow: 0 0 20px rgba(255,255,255,0.8), 0 0 30px rgba(34,197,94,0.6); }
        }
        @keyframes cel-bounce {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-6px) scale(1.05); }
        }
        .cel-fade-in { animation: cel-fade-in 0.3s ease-out; }
        .cel-fade-out { animation: cel-fade-out 0.3s ease-in; }
        .cel-glow { animation: cel-glow 3s ease-in-out infinite; }
        .cel-bounce { animation: cel-bounce 2s ease-in-out infinite; }
      `}</style>

      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
        {/* Backdrop */}
        <div
          className={`${isVisible ? 'cel-fade-in' : 'cel-fade-out'} absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto`}
          onClick={handleClose}
        />

        {/* Popup */}
        <div className={`${isVisible ? 'cel-fade-in' : 'cel-fade-out'} relative z-20 bg-gradient-to-br from-green-900/95 via-emerald-900/95 to-teal-900/95 backdrop-blur-md rounded-3xl p-10 border-2 border-green-400/50 shadow-2xl pointer-events-auto max-w-md w-full mx-4`}>
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 text-white rounded-full transition-all duration-200 hover:scale-110 border border-white/20"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="text-center">
            {/* Success Icon */}
            <div className="mb-5">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto border-4 border-green-400/50">
                <span className="text-3xl cel-bounce cel-glow text-green-400 font-bold">✓</span>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-white mb-2">
              Scan Complete
            </h2>

            {/* Summary text */}
            <p className="text-emerald-200 text-base mb-6 leading-relaxed">
              {summaryText}
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="bg-white/10 rounded-xl py-3 px-2 border border-white/10">
                <div className="text-2xl font-black text-white cel-glow">{elementsScanned}</div>
                <div className="text-xs text-emerald-300 mt-1 font-medium">Elements</div>
              </div>
              <div className="bg-white/10 rounded-xl py-3 px-2 border border-white/10">
                <div className="text-2xl font-black text-white cel-glow">{rulesCompared}</div>
                <div className="text-xs text-emerald-300 mt-1 font-medium">Rules</div>
              </div>
              <div className="bg-green-500/20 rounded-xl py-3 px-2 border border-green-400/30">
                <div className="text-2xl font-black text-green-400 cel-bounce" style={{ animationDelay: '0.2s' }}>{passedCount}</div>
                <div className="text-xs text-green-300 mt-1 font-medium">Rules Passed</div>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={handleViewResults}
              className="w-full py-3 px-6 bg-emerald-500/30 hover:bg-emerald-500/45 text-white font-semibold rounded-xl border border-emerald-300/40 transition-all duration-200 hover:scale-[1.02]"
            >
              View Results
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
